import { Directive, ElementRef, Input, HostListener, Renderer2, OnDestroy } from '@angular/core';

@Directive({
    selector: '[appExpandFullscreen]'
})
export class ExpandFullscreenDirective implements OnDestroy {
    @Input() expandTarget: string = ''; // Element ID or CSS selector

    private isExpanded = false;
    private overlayElement: HTMLElement | null = null;
    private targetElement: HTMLElement | null = null;
    private originalParent: ParentNode | null = null;
    private originalNextSibling: Node | null = null;
    private backdropElement: HTMLElement | null = null;
    private closeButton: HTMLElement | null = null;

    constructor(
        private elementRef: ElementRef,
        private renderer: Renderer2
    ) { }

    @HostListener('click', ['$event'])
    onExpandClick(event: Event): void {
        event.stopPropagation();
        event.preventDefault();
        this.toggleFullscreen();
    }

    private toggleFullscreen(): void {
        if (this.isExpanded) {
            this.closeFullscreen();
        } else {
            this.openFullscreen();
        }
    }

    private openFullscreen(): void {
        const targetElement = document.querySelector(this.expandTarget) as HTMLElement;

        if (!targetElement) {
            console.warn(`[ExpandFullscreen] Target element not found: ${this.expandTarget}`);
            return;
        }

        this.isExpanded = true;
        this.targetElement = targetElement;

        // Store original position for restoration
        this.originalParent = targetElement.parentNode;
        this.originalNextSibling = targetElement.nextSibling;

        // Create backdrop
        this.backdropElement = this.renderer.createElement('div');
        this.renderer.addClass(this.backdropElement, 'expand-fullscreen-backdrop');

        // Create overlay container
        this.overlayElement = this.renderer.createElement('div');
        this.renderer.addClass(this.overlayElement, 'expand-fullscreen-overlay');

        // Create header (empty, just for spacing)
        const header = this.renderer.createElement('div');
        this.renderer.addClass(header, 'expand-fullscreen-header');
        this.renderer.appendChild(this.overlayElement, header);

        // Create close button - append to the expand button's location
        this.closeButton = this.renderer.createElement('button');
        this.renderer.addClass(this.closeButton, 'expand-btn');
        this.renderer.addClass(this.closeButton, 'collapse-btn-fullscreen');
        this.renderer.setProperty(this.closeButton, 'innerHTML', '<i class="fas fa-compress"></i>');
        this.renderer.setAttribute(this.closeButton, 'type', 'button');
        this.renderer.setAttribute(this.closeButton, 'title', 'Exit fullscreen');

        // Attach close handler directly to button
        this.renderer.listen(this.closeButton, 'click', (e: any) => {
            e.stopPropagation();
            e.preventDefault();
            this.closeFullscreen();
        });

        // Inject close button into the section header after the expand button
        setTimeout(() => {
            const sectionHeader = this.targetElement?.querySelector('.section-header');
            if (sectionHeader) {
                const expandBtn = sectionHeader.querySelector('.expand-btn');
                if (expandBtn && expandBtn.parentNode) {
                    // Hide expand button instead of removing it
                    this.renderer.setStyle(expandBtn, 'display', 'none');
                    // Add close button next to the hidden expand button
                    this.renderer.appendChild(expandBtn.parentNode, this.closeButton);
                }
            }
        }, 0);

        // Create content wrapper
        const contentWrapper = this.renderer.createElement('div');
        this.renderer.addClass(contentWrapper, 'expand-fullscreen-content');

        // Move target element to content wrapper (not clone, move)
        this.renderer.appendChild(contentWrapper, targetElement);
        this.renderer.appendChild(this.overlayElement, contentWrapper);

        // Add overlay to body
        this.renderer.appendChild(document.body, this.backdropElement);
        this.renderer.appendChild(document.body, this.overlayElement);

        // Trigger animation
        setTimeout(() => {
            this.renderer.addClass(this.backdropElement, 'active');
            this.renderer.addClass(this.overlayElement, 'active');
        }, 10);
    }

    private closeFullscreen(): void {
        if (!this.isExpanded || !this.overlayElement || !this.targetElement) return;

        this.isExpanded = false;

        // Remove active classes to trigger animation
        if (this.backdropElement) {
            this.renderer.removeClass(this.backdropElement, 'active');
        }
        if (this.overlayElement) {
            this.renderer.removeClass(this.overlayElement, 'active');
        }

        // Wait for animation to complete, then remove elements and restore target
        setTimeout(() => {
            // Show the expand button and remove the close button
            if (this.targetElement) {
                const sectionHeader = this.targetElement.querySelector('.section-header');
                if (sectionHeader) {
                    const expandBtn = sectionHeader.querySelector('.expand-btn');
                    const closeBtn = sectionHeader.querySelector('.collapse-btn-fullscreen');

                    // Show expand button
                    if (expandBtn) {
                        this.renderer.setStyle(expandBtn, 'display', '');
                    }

                    // Remove close button
                    if (closeBtn && closeBtn.parentNode) {
                        this.renderer.removeChild(closeBtn.parentNode, closeBtn);
                    }
                }
            }

            // Move target element back to original position
            if (this.targetElement && this.originalParent) {
                if (this.originalNextSibling) {
                    this.renderer.insertBefore(this.originalParent, this.targetElement, this.originalNextSibling);
                } else {
                    this.renderer.appendChild(this.originalParent, this.targetElement);
                }
            }

            // Remove overlay elements
            if (this.backdropElement && this.backdropElement.parentNode) {
                this.renderer.removeChild(document.body, this.backdropElement);
            }
            if (this.overlayElement && this.overlayElement.parentNode) {
                this.renderer.removeChild(document.body, this.overlayElement);
            }

            this.backdropElement = null;
            this.overlayElement = null;
            this.closeButton = null;
            this.targetElement = null;
        }, 300);
    }

    ngOnDestroy(): void {
        this.closeFullscreen();
    }
}

